import React, { useEffect, useId, useRef, useState } from 'react';

export default function OperatorMultiSelect({
  value,
  users,
  loading,
  error,
  disabled,
  onChange,
}) {
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(0);
  const root = useRef(null);
  const input = useRef(null);
  const listId = useId();

  useEffect(() => {
    const closeOutside = (event) => {
      if (root.current && !root.current.contains(event.target)) {
        setOpen(false);
      }
    };
    document.addEventListener('pointerdown', closeOutside);
    return () => document.removeEventListener('pointerdown', closeOutside);
  }, []);

  const selected = [...new Set(
    String(value ?? '')
      .split(/[,\s;]+/)
      .map((id) => id.trim())
      .filter(Boolean),
  )];

  const employees = Array.isArray(users) ? users : [];
  const byId = new Map(
    employees.map((user) => [String(user.id), user]),
  );

  const nameOf = (user) => [
    user?.last_name,
    user?.name,
  ].filter(Boolean).join(' ').trim();

  const term = query.trim().toLocaleLowerCase('ru');

  const available = employees
    .filter((user) => {
      const id = String(user.id ?? '');
      if (!id || selected.includes(id)) return false;
      if (!term) return true;
      return [
        nameOf(user),
        user.work_position || '',
        id,
      ].join(' ').toLocaleLowerCase('ru').includes(term);
    })
    .sort((a, b) => (
      nameOf(a).localeCompare(nameOf(b), 'ru')
    ))
    .slice(0, 12);

  const update = (ids) => {
    onChange([...new Set(ids)].join(', '));
  };

  const add = (id) => {
    if (disabled || selected.includes(id)) return;
    update([...selected, id]);
    setQuery('');
    setActive(0);
    setOpen(true);
    input.current?.focus();
  };

  const remove = (id) => {
    if (disabled) return;
    update(selected.filter((entry) => entry !== id));
  };

  const keyDown = (event) => {
    if (event.key === 'Escape') {
      setOpen(false);
      return;
    }

    if (event.key === 'ArrowDown') {
      event.preventDefault();
      setOpen(true);
      setActive((current) => Math.min(
        current + 1, Math.max(0, available.length - 1),
      ));
      return;
    }

    if (event.key === 'ArrowUp') {
      event.preventDefault();
      setActive((current) => Math.max(0, current - 1));
      return;
    }

    if (
      event.key === 'Enter'
      && open
      && available.length > 0
    ) {
      event.preventDefault();
      add(String(available[Math.min(
        active, available.length - 1,
      )].id));
    }
  };

  return (
    <div ref={root} className="form-group" style={{ margin: 0 }}>
      <label>Сотрудники Битрикс24</label>

      <div style={{
        border: '1px solid #d6e0ed',
        borderRadius: 9,
        padding: 9,
        background: disabled ? '#f8fafc' : '#fff',
      }}>
        {selected.length > 0 && (
          <div style={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: 6,
            marginBottom: 8,
          }}>
            {selected.map((id) => {
              const person = byId.get(id);
              const title = person ? nameOf(person) : `Сотрудник № ${id}`;

              return (
                <span key={id} style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 6,
                  background: '#e8f2ff',
                  color: '#174777',
                  borderRadius: 6,
                  padding: '5px 7px',
                  fontSize: 13,
                }}>
                  {title || `Сотрудник № ${id}`}
                  <button
                    type="button"
                    disabled={disabled}
                    aria-label={`Убрать ${title}`}
                    onClick={() => remove(id)}
                    style={{
                      cursor: disabled ? 'default' : 'pointer',
                      border: 0,
                      background: 'transparent',
                      color: 'inherit',
                      fontSize: 17,
                      lineHeight: 1,
                    }}
                  >
                    ×
                  </button>
                </span>
              );
            })}
          </div>
        )}

        <input
          ref={input}
          type="text"
          value={query}
          placeholder="Поиск по имени или фамилии"
          disabled={disabled || loading || Boolean(error)}
          autoComplete="off"
          role="combobox"
          aria-autocomplete="list"
          aria-expanded={open && !disabled}
          aria-controls={listId}
          onFocus={() => setOpen(true)}
          onChange={(event) => {
            setQuery(event.target.value);
            setActive(0);
            setOpen(true);
          }}
          onKeyDown={keyDown}
          style={{
            width: '100%',
            minWidth: 0,
            border: 0,
            outline: 'none',
            boxShadow: 'none',
            padding: '6px 3px',
            background: 'transparent',
          }}
        />
      </div>

      {loading && (
        <div style={{ marginTop: 5, fontSize: 12, color: '#64748b' }}>
          Загружаем сотрудников…
        </div>
      )}

      {error && (
        <div role="alert" style={{
          marginTop: 5,
          fontSize: 12,
          color: '#b45309',
        }}>
          Не удалось загрузить список сотрудников.
          Существующие назначения сохранены.
        </div>
      )}

      {open && !loading && !error && !disabled && (
        <div id={listId} role="listbox" style={{
          border: '1px solid #d6e0ed',
          borderRadius: 9,
          marginTop: 5,
          maxHeight: 250,
          overflowY: 'auto',
          background: '#fff',
          boxShadow: '0 8px 20px rgba(15, 23, 42, 0.1)',
        }}>
          {available.length === 0 ? (
            <div style={{ padding: 12, color: '#64748b', fontSize: 13 }}>
              {term
                ? 'Совпадений не найдено'
                : 'Нет сотрудников для выбора'}
            </div>
          ) : available.map((user, index) => (
            <button
              key={String(user.id)}
              type="button"
              role="option"
              aria-selected={index === active}
              onMouseEnter={() => setActive(index)}
              onMouseDown={(event) => event.preventDefault()}
              onClick={() => add(String(user.id))}
              style={{
                display: 'block',
                width: '100%',
                textAlign: 'left',
                padding: '9px 12px',
                border: 0,
                cursor: 'pointer',
                background: index === active
                  ? '#eff6ff' : '#fff',
                color: '#172b4d',
              }}
            >
              <span style={{ display: 'block', fontWeight: 600 }}>
                {nameOf(user) || `Сотрудник № ${user.id}`}
              </span>
              {user.work_position && (
                <span style={{
                  display: 'block',
                  color: '#64748b',
                  fontSize: 12,
                  marginTop: 2,
                }}>
                  {user.work_position}
                </span>
              )}
            </button>
          ))}
        </div>
      )}

      <div style={{
        color: '#718096',
        fontSize: 12,
        marginTop: 5,
      }}>
        Можно выбрать нескольких сотрудников.
        Чтобы снять назначение, нажмите × рядом с именем.
      </div>
    </div>
  );
}
